import {
	cloneElement,
	isValidElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ReactElement,
	type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

interface PopoverProps {
	/** Bouton déclencheur ; reçoit aria-expanded et onClick. */
	trigger: ReactElement<{
		onClick?: () => void;
		"aria-expanded"?: boolean;
		"aria-haspopup"?: string;
	}>;
	children: ReactNode | ((close: () => void) => ReactNode);
	/** Alignement du panneau par rapport au déclencheur. */
	align?: "left" | "right";
	width?: number;
	testId?: string;
}

/** Marge conservée entre le panneau et les bords de la fenêtre. */
const MARGE = 8;

/**
 * Popover ancré à son déclencheur : fermeture au clic extérieur, à Échap
 * et au changement de route (le lien cliqué démonte le contenu).
 *
 * Le panneau est rendu dans `document.body`, pas à côté de son déclencheur.
 * Posé en `absolute` dans le flux, il restait prisonnier du premier ancêtre
 * qui défile : dans un tableau, `.table-wrap` porte `overflow-x: auto`, si
 * bien que le menu de la dernière ligne ne sortait pas de la carte — il
 * l'étirait vers le bas. Un menu d'actions doit flotter au-dessus de la page,
 * jamais la déformer.
 */
export function Popover({
	trigger,
	children,
	align = "right",
	width = 300,
	testId,
}: PopoverProps) {
	const [open, setOpen] = useState(false);
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
	const ancreRef = useRef<HTMLDivElement>(null);
	const panneauRef = useRef<HTMLDivElement>(null);
	const { pathname } = useLocation();

	/**
	 * Place le panneau sous le déclencheur, et au-dessus lorsqu'il déborderait
	 * du bas de la fenêtre — le cas de la dernière ligne d'un tableau.
	 */
	const placer = useCallback(() => {
		const declencheur = ancreRef.current;
		if (!declencheur) return;
		const r = declencheur.getBoundingClientRect();
		const hauteur = panneauRef.current?.offsetHeight ?? 0;

		const placeDessous = window.innerHeight - r.bottom;
		const versLeHaut = hauteur > 0 && placeDessous < hauteur + MARGE && r.top > hauteur + MARGE;
		const top = versLeHaut ? r.top - hauteur - 6 : r.bottom + 6;

		const gauche = align === "right" ? r.right - width : r.left;
		const left = Math.min(
			Math.max(MARGE, gauche),
			Math.max(MARGE, window.innerWidth - width - MARGE),
		);
		setPos({ top, left });
	}, [align, width]);

	// Se referme à tout changement de route (recherche, liens externes au panneau…).
	useEffect(() => {
		setOpen(false);
	}, [pathname]);

	// Mesure après rendu : la hauteur du panneau décide du sens d'ouverture.
	useLayoutEffect(() => {
		if (open) placer();
	}, [open, placer]);

	useEffect(() => {
		if (!open) return;
		const onPointerDown = (e: MouseEvent) => {
			const cible = e.target as Node;
			if (ancreRef.current?.contains(cible)) return;
			if (panneauRef.current?.contains(cible)) return;
			setOpen(false);
		};
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		// Le panneau est posé en coordonnées de fenêtre : il doit suivre tout
		// défilement, y compris celui d'un conteneur interne — d'où la capture.
		const onScroll = () => placer();
		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		window.addEventListener("scroll", onScroll, true);
		window.addEventListener("resize", onScroll);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("scroll", onScroll, true);
			window.removeEventListener("resize", onScroll);
		};
	}, [open, placer]);

	if (!isValidElement(trigger)) return null;

	const close = () => setOpen(false);

	return (
		<div ref={ancreRef} style={{ display: "inline-flex" }}>
			{cloneElement(trigger, {
				onClick: () => setOpen((v) => !v),
				"aria-expanded": open,
				"aria-haspopup": "true",
			})}
			{open &&
				createPortal(
					<div
						ref={panneauRef}
						className="popover popover-flottant"
						style={{
							top: pos?.top ?? 0,
							left: pos?.left ?? 0,
							width,
							// Tant que la position n'est pas mesurée, le panneau est
							// rendu invisible : sans cela il apparaîtrait un instant
							// dans le coin de l'écran avant de sauter à sa place.
							visibility: pos ? "visible" : "hidden",
						}}
						role="menu"
						data-testid={testId}
					>
						{typeof children === "function" ? children(close) : children}
					</div>,
					document.body,
				)}
		</div>
	);
}
